import { Coins, PencilLine, RefreshCcw, Scale } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { InfoNote } from '@/components/common/Panel'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../shared/format'
import { Footer } from './components/AccountForms'
import { PeriodSelect, Section } from './components/FinanceBits'
import { Field, FormSection } from './components/FormBits'
import { CURRENCIES, fmtAmount, FX_RATE_MANAGE, FX_REVALUE, useFxRates, useRefreshRates, useRevaluations, useRunRevaluation, useSaveManualRate } from './fx'
import { toastError } from './ui'

const lastMonth = () => {
  const d = new Date()
  d.setDate(0)
  return d.toISOString().slice(0, 7)
}

/** Döviz ve altın kurları (TCMB günlük bülteni, gram altın, elle giriş) ve ay sonu kur değerlemesi. */
export function FxPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const rates = useFxRates()
  const refresh = useRefreshRates()
  const [manual, setManual] = useState(false)
  const shown = (rates.data ?? []).filter((r) => r.currency !== 'TRY')

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.fx.title')}
        description={t('finance.fx.subtitle')}
        actions={
          can(FX_RATE_MANAGE) ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={refresh.isPending}
                onClick={() =>
                  refresh.mutate(undefined, {
                    onSuccess: (r) =>
                      r.errors?.length ? toast.warning(t('finance.fx.refreshPartial'), { description: r.errors.join(' · ') }) : toast.success(t('finance.fx.refreshed')),
                    onError: toastError,
                  })
                }
              >
                <RefreshCcw className={cn(refresh.isPending && 'animate-spin')} />
                {t('finance.fx.refresh')}
              </Button>
              <Button onClick={() => setManual(true)}>
                <PencilLine />
                {t('finance.fx.manual')}
              </Button>
            </div>
          ) : null
        }
      />
      <div className="space-y-4">
        <Section title={t('finance.fx.latest')} icon={Coins} bodyClassName="p-0">
          {rates.isError ? (
            <ErrorState error={rates.error} onRetry={() => void rates.refetch()} />
          ) : rates.isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : !shown.length ? (
            <EmptyState icon={Coins} title={t('finance.fx.empty')} description={t('finance.fx.emptyHint')} />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('finance.fx.currency')}</TH>
                  <TH className="text-right">{t('finance.fx.buying')}</TH>
                  <TH className="text-right">{t('finance.fx.selling')}</TH>
                  <TH>{t('finance.fx.date')}</TH>
                  <TH>{t('finance.fx.source')}</TH>
                </TR>
              </THead>
              <TBody>
                {shown.map((r) => (
                  <TR key={r.currency}>
                    <TD className="font-medium">{t(`finance.fx.currencies.${r.currency}`)}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.buying)}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.selling)}</TD>
                    <TD>{fmtDate(r.date)}</TD>
                    <TD>
                      <Badge tone={r.source === 'manual' ? 'info' : 'neutral'}>{t(`finance.fx.sources.${r.source}`)}</Badge>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Section>
        <InfoNote>{t('finance.fx.sourceNote')}</InfoNote>
        <RevaluationSection canRun={can(FX_REVALUE)} />
      </div>
      {manual ? <ManualRateModal onDone={() => setManual(false)} /> : null}
    </>
  )
}

function RevaluationSection({ canRun }: { canRun: boolean }) {
  const { t } = useTranslation()
  const [period, setPeriod] = useState(lastMonth)
  const list = useRevaluations(period)
  const run = useRunRevaluation()
  // Çalıştırma sonucu (atlanan hesap gerekçeleriyle) aynı dönemdeyse onu, değilse kayıtlı değerlemeleri göster.
  const rows = run.data && run.variables === period ? run.data : (list.data ?? [])
  return (
    <Section
      title={t('finance.fx.revaluation')}
      icon={Scale}
      bodyClassName="p-0"
      actions={
        <div className="flex flex-wrap gap-2">
          <PeriodSelect value={period} onChange={setPeriod} />
          {canRun ? (
            <Button
              size="sm"
              disabled={run.isPending}
              onClick={() =>
                run.mutate(period, {
                  onSuccess: (rows) => {
                    const done = rows.filter((r) => !r.skipped).length
                    toast.success(t('finance.fx.revalued', { count: done }))
                  },
                  onError: toastError,
                })
              }
            >
              {t('finance.fx.runRevaluation')}
            </Button>
          ) : null}
        </div>
      }
    >
      <p className="text-muted-foreground border-b px-4 py-2 text-xs">{t('finance.fx.revaluationHint')}</p>
      {list.isLoading ? (
        <Skeleton className="m-4 h-24" />
      ) : !rows.length ? (
        <EmptyState icon={Scale} title={t('finance.fx.noRevaluation')} />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('finance.fx.account')}</TH>
              <TH className="text-right">{t('finance.fx.balance')}</TH>
              <TH className="text-right">{t('finance.fx.rate')}</TH>
              <TH className="text-right">{t('finance.fx.book')}</TH>
              <TH className="text-right">{t('finance.fx.market')}</TH>
              <TH className="text-right">{t('finance.fx.diff')}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR key={r.accountId + r.period}>
                <TD className="font-medium">{r.accountName}</TD>
                {r.skipped ? (
                  <TD colSpan={5} className="text-muted-foreground text-xs">
                    {r.skipped}
                  </TD>
                ) : (
                  <>
                    <TD className="text-right tabular-nums">{fmtAmount(r.balance, r.currency)}</TD>
                    <TD className="text-right tabular-nums">{r.rate}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.bookTry)}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.marketTry)}</TD>
                    <TD className={cn('text-right font-medium tabular-nums', Number(r.diffTry) > 0 ? 'text-success' : Number(r.diffTry) < 0 ? 'text-destructive' : '')}>
                      {fmtMoney(r.diffTry)}
                    </TD>
                  </>
                )}
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Section>
  )
}

function ManualRateModal({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveManualRate()
  const [currency, setCurrency] = useState('USD')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [buying, setBuying] = useState('')
  const [selling, setSelling] = useState('')
  const valid = Number(buying.replace(',', '.')) > 0
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onDone()}
      dismissible={!save.isPending}
      icon={<PencilLine />}
      title={t('finance.fx.manualTitle')}
      description={t('finance.fx.manualHint')}
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid) return
        save.mutate(
          { currency, date, buying, selling },
          {
            onSuccess: () => {
              toast.success(t('finance.fx.manualSaved'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={save.isPending} disabled={!valid} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="mr-cur" label={t('finance.fx.currency')}>
          <Select id="mr-cur" value={currency} onChange={setCurrency} options={CURRENCIES.filter((c) => c !== 'TRY').map((c) => ({ value: c, label: t(`finance.fx.currencies.${c}`) }))} />
        </Field>
        <Field id="mr-date" label={t('finance.fx.date')}>
          <DateField id="mr-date" value={date} onChange={setDate} clearable={false} />
        </Field>
        <Field id="mr-buy" label={t('finance.fx.buying')}>
          <Input id="mr-buy" inputMode="decimal" value={buying} onChange={(e) => setBuying(e.target.value)} />
        </Field>
        <Field id="mr-sell" label={t('finance.fx.selling')} hint={t('finance.fx.sellingHint')}>
          <Input id="mr-sell" inputMode="decimal" value={selling} onChange={(e) => setSelling(e.target.value)} />
        </Field>
      </FormSection>
    </Modal>
  )
}
